import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { schedulingFetchGet, schedulingFetchPost } from 'app/utils/request/scheduling/fetch';
import { getColorCode, generateCurrencySymbolPriceAbbreviation } from 'app/routes/BuildProgram/Pricing/utils';
import SquaredColorOption from '../SquaredColorOption';
import SearchBar from 'app/components/inputs/SearchBar';
import ModalLayout from 'app/components/Layouts/ModalLayout/ModalLayout';
import Checkmark from '../../../../public/images/green-checkmark.svg';
import ArrowRightIcon from '../../../../public/images/arrow-right.svg';
import styles from './styles.module.scss';

interface TransferRegistrantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTransferred: (response: any) => void;
  registrationId: number;
  studentName: string;
  sourcePrice: number;
  sourceIsWaitlisted: boolean;
}

interface TransferableProgram {
  id: number;
  title: string;
  subtitle: string;
  attendance: string;
  startTime: string;
  endTime: string;
  registrationStatusCode: string;
  registrationStatus: string;
  capacity: number;
  spotsBooked: number;
  externalThumbnailUrl: string | null;
  recurrenceGroupId: number | null;
  price: number;
  priceCurrencyIso: string;
}

// A recurring clinic's occurrences are grouped under a single parent entry the
// coach drills into, instead of listing every occurrence separately (CFTB-3374).
interface ParentGroup {
  recurrenceGroupId: number;
  title: string;
  subtitle: string;
  externalThumbnailUrl: string | null;
  events: TransferableProgram[];
}

type ListEntry =
  | { type: 'group'; group: ParentGroup }
  | { type: 'single'; program: TransferableProgram };

type Step = 'confirm' | 'select' | 'waitlist' | 'success';

export default function TransferRegistrantModal({
  isOpen,
  onClose,
  onTransferred,
  registrationId,
  studentName,
  sourcePrice,
  sourceIsWaitlisted,
}: TransferRegistrantModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('confirm');
  const [programs, setPrograms] = useState<TransferableProgram[]>([]);
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<TransferableProgram | null>(null);
  const [activeGroup, setActiveGroup] = useState<ParentGroup | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) { return; }
    setStep('confirm');
    setLoading(true);
    setSelectedProgram(null);
    setActiveGroup(null);
    setSearch('');

    schedulingFetchGet(`/student_program_registrations/${registrationId}/transferable_programs`)
      .then((data) => setPrograms(data))
      .catch(() => setPrograms([]))
      .finally(() => setLoading(false));
  }, [isOpen, registrationId]);

  // Group recurring-clinic occurrences under one parent entry; everything else
  // (standalone programs, collapsed series) stays a directly selectable row.
  const entries = useMemo<ListEntry[]>(() => {
    const seen = new Set<number>();
    const result: ListEntry[] = [];

    programs.forEach((program) => {
      const groupId = program.recurrenceGroupId;
      if (program.attendance !== 'clinic' || groupId == null) {
        result.push({ type: 'single', program });
        return;
      }
      if (seen.has(groupId)) { return; }

      const events = programs.filter(
        (p) => p.attendance === 'clinic' && p.recurrenceGroupId === groupId,
      );

      // A single available occurrence needs no drill-down — show it as a plain row.
      if (events.length < 2) {
        result.push({ type: 'single', program });
        return;
      }

      seen.add(groupId);
      result.push({
        type: 'group',
        group: {
          recurrenceGroupId: groupId,
          title: program.title,
          subtitle: program.subtitle,
          externalThumbnailUrl: program.externalThumbnailUrl,
          events,
        },
      });
    });

    return result;
  }, [programs]);

  // Plain title/subtitle search over the top-level list (no filters — CN-2214).
  const visibleEntries = useMemo<ListEntry[]>(() => {
    const term = search.trim().toLowerCase();
    if (!term) { return entries; }

    return entries.filter((entry) => {
      const { title, subtitle } =
        entry.type === 'group' ? entry.group : entry.program;
      return `${title} ${subtitle ?? ''}`.toLowerCase().includes(term);
    });
  }, [entries, search]);

  const isFull = (program: TransferableProgram) =>
    program.capacity - program.spotsBooked <= 0;

  // Full programs stay selectable; clicking Move first warns the coach that the
  // athlete will land on the program's waitlist (CN-2214 review feedback).
  const handleMoveClick = () => {
    if (!selectedProgram) { return; }
    if (isFull(selectedProgram)) {
      setStep('waitlist');
      return;
    }
    handleTransfer(selectedProgram);
  };

  const handleTransfer = async (program: TransferableProgram) => {
    setTransferring(true);
    try {
      await schedulingFetchPost(
        `/student_program_registrations/${registrationId}/transfer`,
        { target_program_id: program.id },
      );
      setStep('success');
    } catch (error) {
      console.error('Transfer failed:', error);
      if (error?.status === 422) {
        toast.error(error.error || t("Transfer failed. The athlete may already be registered in this program."));
      } else {
        toast.error(t("Failed to transfer athlete. Please try again."));
      }
      // Return to the list so the coach can retry (e.g. after a waitlist confirm).
      setStep('select');
    } finally {
      setTransferring(false);
    }
  };

  const formatDateRange = (start: string, end: string) => {
    if (!start) { return ''; }
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : null;
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    const startLabel = startDate.toLocaleDateString(undefined, opts);
    // Only show a range when the event actually spans multiple calendar days;
    // single-day events (e.g. a recurring clinic occurrence) show one date (CFTB-3373).
    if (endDate && endDate.toDateString() !== startDate.toDateString()) {
      return `${startLabel} - ${endDate.toLocaleDateString(undefined, opts)}`;
    }
    return startLabel;
  };

  const renderEventCard = (program: TransferableProgram) => {
    // Full programs are valid transfer targets (a coach may overfill them), so
    // they render identically to programs with open slots — no dimming (CFTB-3397).
    const isSelected = selectedProgram?.id === program.id;

    return (
      <div
        key={program.id}
        className={`${styles.program_card} ${isSelected ? styles.selected : ''}`}
        onClick={() => setSelectedProgram(program)}
        data-testid="transfer-program-card"
      >
        <div className={styles.avatar}>
          {program.externalThumbnailUrl && <img src={program.externalThumbnailUrl} alt="" />}
        </div>

        <div className={styles.program_info}>
          <div className={styles.program_title}>{program.title}</div>
          {program.subtitle && <div className={styles.program_subtitle}>{program.subtitle}</div>}
          <div className={styles.program_date}>
            {formatDateRange(program.startTime, program.endTime)}
          </div>
        </div>

        <SquaredColorOption
          color={getColorCode(program.registrationStatusCode)}
          text={program.registrationStatus}
          customContainer={styles.status_badge}
        />

        <span className={styles.spots}>
          {program.spotsBooked}/{program.capacity}
        </span>
      </div>
    );
  };

  const renderParentCard = (group: ParentGroup) => (
    <div
      key={`group-${group.recurrenceGroupId}`}
      className={styles.program_card}
      onClick={() => {
        setActiveGroup(group);
        setSelectedProgram(null);
      }}
      data-testid="transfer-parent-card"
    >
      <div className={styles.avatar}>
        {group.externalThumbnailUrl && <img src={group.externalThumbnailUrl} alt="" />}
      </div>

      <div className={styles.program_info}>
        <div className={styles.program_title}>{group.title}</div>
        {group.subtitle && <div className={styles.program_subtitle}>{group.subtitle}</div>}
        <div className={styles.program_date}>
          {t('{{count}} events', { count: group.events.length })}
        </div>
      </div>

      <img src={ArrowRightIcon} alt="" className={styles.chevron} />
    </div>
  );

  if (!isOpen) { return null; }

  if (step === 'confirm') {
    return (
      <ModalLayout isOpen={isOpen} classNames={styles.small_modal}>
        <div className={styles.small_content}>
          <div className={styles.close_button} onClick={onClose}>
            <i className="ico ico-x" />
          </div>
          <div className={styles.confirm_icon}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4 12h15M13 6l6 6-6 6"
                stroke="#039BE5"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className={styles.small_title}>
            {t('Are you sure you want to move {{studentName}} to another program?', { studentName })}
          </h2>
          <p className={styles.small_text}>
            {t('The athlete will be removed from this program and enrolled in a different one.')}
          </p>
          <p className={styles.small_text}>
            {t('No refund will be issued, and no additional charges will be applied. Go to Stripe to manage the payments related to this change.')}
          </p>
          <button
            className={styles.primary_button}
            onClick={() => setStep('select')}
            data-testid="transfer-confirm-move-button"
          >
            {t('Move')}
          </button>
          <button
            className={styles.secondary_button}
            onClick={onClose}
            data-testid="transfer-confirm-cancel-button"
          >
            {t('Cancel')}
          </button>
        </div>
      </ModalLayout>
    );
  }

  if (step === 'success') {
    // Payment is left untouched by a transfer (CFTB-3366), so a price-differing
    // target isn't reconciled automatically — just surface it so the coach knows
    // they're responsible for handling the difference (CFTB-3400). Not applicable
    // when the source was waitlisted: that athlete pays the target's price
    // directly via waitlist promotion (CFTB-3399), so there's no difference left
    // for the coach to handle (CFTB-3401).
    const priceDiffCents = selectedProgram && !sourceIsWaitlisted
      ? Math.round(selectedProgram.price * 100) - Math.round(sourcePrice * 100)
      : 0;

    return (
      <ModalLayout isOpen={isOpen} classNames={styles.small_modal}>
        <div className={styles.small_content}>
          <div className={styles.success_icon}>
            <img src={Checkmark} width={48} height={48} alt="" />
          </div>
          <h2 className={styles.small_title} data-testid="transfer-success-title">
            {t('Athlete Moved')}
          </h2>
          <p className={styles.small_text}>
            <Trans
              i18nKey="transfer_athlete_moved_success"
              values={{ studentName, programTitle: selectedProgram?.title }}
              components={{ strong: <strong className={styles.emphasis} /> }}
            />
          </p>
          {selectedProgram && priceDiffCents !== 0 && (
            <p className={styles.small_text}>
              <Trans
                i18nKey={priceDiffCents > 0 ? 'transfer_price_increase_notice' : 'transfer_price_decrease_notice'}
                values={{
                  newPrice: generateCurrencySymbolPriceAbbreviation(
                    selectedProgram.priceCurrencyIso, String(selectedProgram.price),
                  ),
                  diff: generateCurrencySymbolPriceAbbreviation(
                    selectedProgram.priceCurrencyIso, String(Math.abs(priceDiffCents) / 100),
                  ),
                }}
                components={{ strong: <strong className={styles.emphasis} /> }}
              />
            </p>
          )}
          <button
            className={styles.primary_button}
            onClick={() => {
              onTransferred(null);
              onClose();
            }}
          >
            {t('Ok')}
          </button>
        </div>
      </ModalLayout>
    );
  }

  if (step === 'waitlist') {
    return (
      <ModalLayout isOpen={isOpen} classNames={styles.small_modal}>
        <div className={styles.small_content}>
          <div className={styles.close_button} onClick={onClose}>
            <i className="ico ico-x" />
          </div>
          <div className={styles.confirm_icon}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 8v5M12 16.5v.5M10.3 3.9 2.4 18a1.9 1.9 0 0 0 1.7 2.9h15.8a1.9 1.9 0 0 0 1.7-2.9L13.7 3.9a1.9 1.9 0 0 0-3.4 0Z"
                stroke="#039BE5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className={styles.small_title} data-testid="transfer-waitlist-title">
            {t('This program is full')}
          </h2>
          <p className={styles.small_text}>
            {/* A waitlisted source athlete lands on the target's waitlist rather
                than overfilling it directly (CFTB-3372/3399), so the overfill
                copy is wrong for that case (CFTB-3401). */}
            <Trans
              i18nKey={sourceIsWaitlisted ? 'transfer_waitlist_first_confirm' : 'transfer_overfill_confirm'}
              values={{ studentName, programTitle: selectedProgram?.title }}
              components={{ strong: <strong className={styles.emphasis} /> }}
            />
          </p>
          <button
            className={styles.primary_button}
            disabled={transferring}
            onClick={() => selectedProgram && handleTransfer(selectedProgram)}
            data-testid="transfer-move-anyway-button"
          >
            {transferring ? t('Moving...') : t('Move anyway')}
          </button>
          <button
            className={styles.secondary_button}
            disabled={transferring}
            onClick={() => setStep('select')}
            data-testid="transfer-waitlist-back-button"
          >
            {t('Back')}
          </button>
        </div>
      </ModalLayout>
    );
  }

  return (
    <ModalLayout isOpen={isOpen} classNames={styles.modal}>
      <div className={styles.content}>
        <div className={styles.close_button} onClick={onClose}>
          <i className="ico ico-x" />
        </div>

        <div className={styles.body}>
          <div className={styles.header}>
            {activeGroup ? (
              <>
                <button
                  className={styles.back_button}
                  onClick={() => {
                    setActiveGroup(null);
                    setSelectedProgram(null);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M15 18l-6-6 6-6"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t('Back')}
                </button>
                <h2 className={styles.title}>{activeGroup.title}</h2>
                <p className={styles.description}>
                  {t('Select the event to which you want to transfer this athlete.')}
                </p>
              </>
            ) : (
              <>
                <h2 className={styles.title}>{t('Move {{studentName}} to...', { studentName })}</h2>
                <p className={styles.description}>
                  {t('Select the program to which you want to transfer this athlete. The athlete will neither receive a refund nor be charged again for this program.')}
                </p>
              </>
            )}
          </div>

          {!loading && !activeGroup && entries.length > 0 && (
            <SearchBar
              className={styles.search}
              placeholder={t('Search programs')}
              value={search}
              onChange={(value) => setSearch(value)}
            />
          )}

          <div className={styles.program_list}>
            {loading && <div className={styles.loading}>{t('Loading...')}</div>}
            {!loading && !activeGroup && entries.length === 0 && (
              <div className={styles.empty} data-testid="transfer-empty">
              {t('No programs available for transfer')}
            </div>
            )}
            {!loading && !activeGroup && entries.length > 0 && visibleEntries.length === 0 && (
              <div className={styles.empty} data-testid="transfer-no-search-match">
                {t('No programs match your search')}
              </div>
            )}
            {!loading && activeGroup && activeGroup.events.map(renderEventCard)}
            {!loading && !activeGroup &&
              visibleEntries.map((entry) =>
                entry.type === 'group'
                  ? renderParentCard(entry.group)
                  : renderEventCard(entry.program)
              )}
          </div>
        </div>

        <div className={styles.footer}>
          <button className={styles.secondary_button} onClick={onClose}>
            {t('Cancel')}
          </button>
          <button
            className={styles.primary_button}
            disabled={!selectedProgram || transferring}
            onClick={handleMoveClick}
            data-testid="transfer-move-button"
          >
            {transferring ? t('Moving...') : t('Move')}
          </button>
        </div>
      </div>
    </ModalLayout>
  );
}
